// The wellbeing module's reads and writes, all on the Day record (one per
// date): the evening ("End of day", the reset sessions, the smoking log, one
// timed alarm), the nightly check-in, and the numbers a day is summarised by.
// Nothing here counts what was missed, and no day is marked as failed: a day
// without a record is a day without a record.
import { db } from '@/runtime'
import type { DayResource } from '@/kinds/day/apply'
import type { DaySpec, DayReset, ResetSize } from '@/kinds/day/schema'
import { RESET_SIZES } from '@/kinds/day/schema'
import { addDays, dateOf, todayLocal } from '@/lib/time'
import { markDay, nightLength, nowIso, type DayPatch } from '@/lib/day'

/** What the reset says when the timer runs out. */
export const STAND_UP = 'Stand up: shower, walk, or food.'
/** The nap cap, fixed: the brief's thirty minutes. */
export const NAP_CAP_MIN = 30
export const NAP_UP = 'Thirty minutes. Up you get.'

/** HH:MM on the 24-hour clock: the numbers are read across days, so they stay narrow and sortable. */
const clock = (iso: string) => {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/**
 * The evening belongs to the day it started on: an entry made before 05:00
 * goes on yesterday's record, as a sleep does, so a late smoke or a late
 * check-in lands with the evening it was part of.
 */
export const eveningOf = (now: number): string => {
  const d = new Date(now)
  return d.getHours() < 5 ? addDays(dateOf(d), -1) : dateOf(d)
}

const dayRecord = async (date: string): Promise<DayResource | undefined> => {
  const days = (await db.resources.where('kind').equals('Day').toArray()) as DayResource[]
  return days.find((d) => !d.deleted_at && d.spec.date === date)
}

/** Patch a day from what it holds now (for the arrays: resets, smokes). */
const patchDay = async (date: string, build: (spec: Partial<DaySpec>) => DayPatch) => {
  const cur = await dayRecord(date)
  return markDay(date, build(cur?.spec ?? {}))
}

/** "End of day": the event that starts the evening. Pressing it twice changes nothing. */
export const endDay = (date: string) =>
  patchDay(date, (spec) => (spec.ended_at ? {} : { ended_at: nowIso() }))

/** One smoke, now, in the owner's unit. */
export const logSmoke = (date: string, amount = 1) =>
  patchDay(date, (spec) => ({ smokes: [...(spec.smokes ?? []), { at: nowIso(), amount }] }))

/** Take the last smoke back (a mis-tap). */
export const unlogSmoke = (date: string) =>
  patchDay(date, (spec) => ({ smokes: (spec.smokes ?? []).slice(0, -1) }))

/**
 * A reset begins: logged as not yet completed, with the record's alarm set
 * for its end so a closed app still hears "Stand up". Ends the day too if
 * that was not pressed, since the chair is what ending the day is for.
 */
export const beginReset = (date: string, size: ResetSize, audio: 'noise' | 'none') => {
  const start = nowIso()
  const end = new Date(Date.parse(start) + RESET_SIZES[size] * 60_000).toISOString()
  return patchDay(date, (spec) => ({
    ended_at: spec.ended_at ?? start,
    resets: [...(spec.resets ?? []), { start, size, audio, completed: false }],
    alarm_at: end,
    alarm_text: STAND_UP,
    alarm_url: '/reset',
  }))
}

const NO_ALARM: DayPatch = { alarm_at: null, alarm_text: null, alarm_url: null }

/** A reset ends, complete when the timer ran out, otherwise stopped; the alarm goes with it. */
export const endReset = (date: string, completed: boolean) =>
  patchDay(date, (spec) => {
    const resets = [...(spec.resets ?? [])]
    const last = resets[resets.length - 1]
    if (!last || last.ended) return {}
    resets[resets.length - 1] = { ...last, completed, ended: nowIso() }
    return { resets, ...NO_ALARM }
  })

/** A nap begins: the cap alarm in thirty minutes. */
export const beginNap = (date: string) =>
  patchDay(date, () => ({
    alarm_at: new Date(Date.now() + NAP_CAP_MIN * 60_000).toISOString(),
    alarm_text: NAP_UP,
    alarm_url: '/wellbeing',
  }))

/** Up before the cap: the alarm goes. */
export const endNap = (date: string) => patchDay(date, (spec) => (spec.alarm_text === NAP_UP ? NO_ALARM : {}))

/** A nap cap that is still to come, as "up at HH:MM". */
export const napCap = (spec?: DaySpec, now = Date.now()): string | undefined =>
  spec?.alarm_text === NAP_UP && spec.alarm_at && Date.parse(spec.alarm_at) > now
    ? clock(spec.alarm_at)
    : undefined

export interface CheckinValues {
  anxiety: number
  unreality: number
  mood: number
  nap_min: number
  exercise_min: number
  note: string
}

/** The nightly check-in, saved with when it was saved. */
export const saveCheckin = (date: string, v: CheckinValues) =>
  markDay(date, {
    anxiety: v.anxiety,
    unreality: v.unreality,
    mood: v.mood,
    nap_min: v.nap_min,
    exercise_min: v.exercise_min,
    note: v.note.trim(),
    checked_in_at: nowIso(),
  })

/** A day's numbers, as the page and the export read them. */
export interface DaySummary {
  date: string
  anxiety?: number
  unreality?: number
  mood?: number
  /** "7h 32m", when both ends of the night are known */
  night?: string
  napMin: number
  exerciseMin: number
  smokeTotal: number
  /** local HH:MM of the first smoke */
  firstSmoke?: string
  /** the first smoke came after a reset had started; undefined with no smoke */
  chairFirst?: boolean
  /** a reset ran its whole length */
  resetDone: boolean
  resetStart?: string
  ended?: string
  checkedIn: boolean
  note?: string
}

export const summarise = (spec: DaySpec, next?: DaySpec): DaySummary => {
  const smokes = [...(spec.smokes ?? [])].sort((a, b) => a.at.localeCompare(b.at))
  const first = smokes[0]
  const resets = spec.resets ?? []
  const firstReset = resets.map((r) => r.start).sort()[0]
  return {
    date: spec.date,
    anxiety: spec.anxiety,
    unreality: spec.unreality,
    mood: spec.mood,
    // the night pairs lights out with the next morning's wake
    night: nightLength(spec.slept_at, next?.woke_at),
    napMin: spec.nap_min ?? 0,
    exerciseMin: spec.exercise_min ?? 0,
    smokeTotal: smokes.reduce((n, s) => n + s.amount, 0),
    firstSmoke: first ? clock(first.at) : undefined,
    chairFirst: first ? !!firstReset && firstReset <= first.at : undefined,
    resetDone: resets.some((r) => r.completed),
    resetStart: firstReset ? clock(firstReset) : undefined,
    ended: spec.ended_at ? clock(spec.ended_at) : undefined,
    checkedIn: !!spec.checked_in_at,
    note: spec.note || undefined,
  }
}

/** The last `n` dates ending today, newest first, each with its record if any. */
export const recentDays = (days: DayResource[], n: number, today = todayLocal()) => {
  const byDate = new Map(days.map((d) => [d.spec.date, d.spec]))
  const out: { date: string; spec?: DaySpec; summary?: DaySummary }[] = []
  for (let i = 0; i < n; i++) {
    const date = addDays(today, -i)
    const spec = byDate.get(date)
    out.push({ date, spec, summary: spec ? summarise(spec, byDate.get(addDays(date, 1))) : undefined })
  }
  return out
}

/** The reset that is still open on a record, if any. */
export const openReset = (spec?: DaySpec): DayReset | undefined => {
  const last = spec?.resets?.[spec.resets.length - 1]
  return last && !last.ended ? last : undefined
}

const csvCell = (v: unknown): string => {
  if (v === undefined || v === null) return ''
  const s = String(v)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/**
 * Every day with a record, oldest first, as CSV: one row a day with the
 * check-in, the night, the smokes (total, first, unit) and the reset. Built
 * on the device from its own copy; nothing leaves unless this is saved.
 */
export const toCsv = (days: DayResource[], unit: string): string => {
  const sorted = [...days].sort((a, b) => a.spec.date.localeCompare(b.spec.date))
  const byDate = new Map(sorted.map((d) => [d.spec.date, d.spec]))
  const head = [
    'date',
    'anxiety',
    'unreality',
    'mood',
    'lights_out',
    'woke',
    'night',
    'nap_min',
    'exercise_min',
    `smokes_${unit.replace(/\W+/g, '_')}`,
    'first_smoke',
    'chair_before_first_smoke',
    'day_ended',
    'reset_start',
    'reset_size',
    'reset_completed',
    'note',
  ]
  const rows = sorted.map((d) => {
    const s = summarise(d.spec, byDate.get(addDays(d.spec.date, 1)))
    const reset = d.spec.resets?.[0]
    return [
      s.date,
      s.anxiety,
      s.unreality,
      s.mood,
      d.spec.slept_at ? clock(d.spec.slept_at) : '',
      d.spec.woke_at ? clock(d.spec.woke_at) : '',
      s.night ?? '',
      s.napMin || '',
      s.exerciseMin || '',
      s.smokeTotal || '',
      s.firstSmoke ?? '',
      s.chairFirst === undefined ? '' : s.chairFirst ? 'yes' : 'no',
      s.ended ?? '',
      s.resetStart ?? '',
      reset?.size ?? '',
      reset ? (reset.completed ? 'yes' : 'no') : '',
      s.note ?? '',
    ]
      .map(csvCell)
      .join(',')
  })
  return [head.join(','), ...rows].join('\n') + '\n'
}
